'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown, Facebook, Linkedin } from 'lucide-react';
import { useTranslations, useLocale } from 'next-intl';
import { useConfig } from '@/hooks/useSiteConfig';
import LanguageSwitcher from '@/components/ui/LanguageSwitcher';

export default function Navbar() {
  const t = useTranslations('nav');
  const tExpertise = useTranslations('expertiseMenu');
  const locale = useLocale();
  const pathname = usePathname();
  const config = useConfig();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null);
  const [mobileSubmenuOpen, setMobileSubmenuOpen] = useState<string | null>(null);

  const expertises = [
    { name: tExpertise('studiesResearch'), href: `/${locale}/expertises/etudes-recherche` },
    { name: tExpertise('monitoringEvaluation'), href: `/${locale}/expertises/suivi-evaluation` },
    { name: tExpertise('csrAdvice'), href: `/${locale}/expertises/rse` },
    { name: tExpertise('projectManagement'), href: `/${locale}/expertises/gestion-projets` },
    { name: tExpertise('dataAnalysis'), href: `/${locale}/expertises/veille-analyse` },
  ];

  const navLinks = [
    { name: t('home'), href: `/${locale}` },
    { name: t('publications'), href: `/${locale}/publications` },
    { name: t('about'), href: `/${locale}/a-propos` },
    { name: t('expertises'), href: `/${locale}/expertises`, submenu: expertises },
    { name: t('formations'), href: `/${locale}/formations` },
    { name: t('references'), href: `/${locale}/references` },
    { name: t('contact'), href: `/${locale}/contact` },
  ];

  // Check if a link is active
  const isLinkActive = (href: string, submenu?: { href: string }[]) => {
    // Exact match for home
    if (href === `/${locale}` && pathname === `/${locale}`) {
      return true;
    }
    // For other pages, check if pathname starts with the href (excluding home)
    if (href !== `/${locale}` && pathname.startsWith(href)) {
      return true;
    }
    // Check submenu items
    if (submenu) {
      return submenu.some(item => pathname.startsWith(item.href));
    }
    return false;
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Lock body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-md shadow-md' : 'bg-transparent'
          }`}
      >
        {/* Desktop/Default Navbar View */}
        <motion.div
          initial={{ y: -100 }}
          animate={{ y: 0 }}
          className="relative z-50"
        >
          <nav className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
            <div className="flex items-center justify-between h-24">
              {/* Logo */}
              <Link href={`/${locale}`} className="flex items-center gap-3">
                <div className="relative w-12 h-12 flex-shrink-0">
                  <Image
                    src="/logo.png"
                    alt="CERAD-CI Logo"
                    fill
                    sizes="48px"
                    className="object-contain"
                    priority
                  />
                </div>
                <div className="hidden sm:block">
                  <span className={`font-montserrat font-bold text-xl ${scrolled ? 'text-cerad-green' : 'text-white'}`}>
                    CERAD-CI
                  </span>
                  <p className={`text-xs ${scrolled ? 'text-gray-500' : 'text-white/80'}`}>
                    Centre de Recherche Action pour le Développement
                  </p>
                </div>
              </Link>

              {/* Desktop Navigation */}
              <div className="hidden lg:flex items-center gap-0.5 xl:gap-1">
                {navLinks.map((link) => {
                  const isActive = isLinkActive(link.href, link.submenu);

                  return (
                    <div
                      key={link.name}
                      className="relative"
                    >
                      <div
                        onMouseEnter={() => link.submenu && setActiveSubmenu(link.name)}
                        onMouseLeave={() => !link.submenu && setActiveSubmenu(null)}
                      >
                        <Link
                          href={link.href}
                          className={`relative px-3 xl:px-4 py-2 rounded-full font-medium text-sm xl:text-base whitespace-nowrap transition-all duration-200 flex items-center gap-1 group ${
                            scrolled
                              ? isActive
                                ? 'text-cerad-green bg-cerad-green/10'
                                : 'text-gray-700 hover:text-cerad-green hover:bg-cerad-green/5'
                              : isActive
                                ? 'text-white bg-white/20'
                                : 'text-white/90 hover:text-white hover:bg-white/10'
                          }`}
                        >
                          {link.name}
                          {link.submenu && <ChevronDown className="w-4 h-4" />}

                          {/* Active indicator - modern underline */}
                          {isActive && (
                            <motion.div
                              layoutId="navbar-active-indicator"
                              className={`absolute -bottom-1 left-1/2 -translate-x-1/2 h-0.5 rounded-full ${
                                scrolled ? 'bg-cerad-green w-8' : 'bg-white w-8'
                              }`}
                              transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                            />
                          )}
                        </Link>
                      </div>

                      {/* Submenu */}
                      <AnimatePresence>
                        {link.submenu && activeSubmenu === link.name && (
                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 10 }}
                            className="absolute top-full left-0 mt-2 w-72 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden"
                            onMouseEnter={() => setActiveSubmenu(link.name)}
                            onMouseLeave={() => setActiveSubmenu(null)}
                          >
                            <div className="p-2">
                              {link.submenu.map((item) => {
                                const isSubmenuActive = pathname.startsWith(item.href);
                                return (
                                  <Link
                                    key={item.name}
                                    href={item.href}
                                    className={`block px-4 py-3 rounded-xl transition-colors relative ${
                                      isSubmenuActive
                                        ? 'bg-cerad-green/10 text-cerad-green font-semibold'
                                        : 'text-gray-700 hover:bg-cerad-green/5 hover:text-cerad-green'
                                    }`}
                                  >
                                    {isSubmenuActive && (
                                      <span className="absolute left-2 top-1/2 -translate-y-1/2 w-1 h-6 bg-cerad-green rounded-full" />
                                    )}
                                    <span className={isSubmenuActive ? 'ml-3' : ''}>
                                      {item.name}
                                    </span>
                                  </Link>
                                );
                              })}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}

                {/* Language Switcher */}
                <LanguageSwitcher scrolled={scrolled} />
              </div>

              {/* Mobile Menu Toggle Button */}
              <button
                onClick={() => setIsOpen(true)}
                className={`lg:hidden p-2 rounded-xl transition-colors ${scrolled ? 'text-cerad-green' : 'text-white'
                  }`}
              >
                <Menu className="w-6 h-6" />
              </button>
            </div>
          </nav>
        </motion.div>
      </header>

      {/* Full-screen Mobile Menu Overlay - Outside header to prevent clipping */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 12, stiffness: 400, mass: 0.2 }}
            className="fixed inset-0 bg-white/70 backdrop-blur-md z-[70] flex flex-col"
          >
            {/* Subtle gradient decoration */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute -top-40 -right-40 w-80 h-80 bg-cerad-green/5 rounded-full blur-3xl" />
              <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-cerad-gold/5 rounded-full blur-3xl" />
            </div>

            {/* Overlay Header */}
            <div className="relative flex items-center justify-between h-20 px-6 border-b border-gray-100 bg-white/20">
              <Link
                href={`/${locale}`}
                onClick={() => setIsOpen(false)}
                className="flex items-center gap-3"
                title={t('homeTitle')}
              >
                <div className="relative w-10 h-10 flex-shrink-0">
                  <Image
                    src="/logo.png"
                    alt="CERAD-CI Logo"
                    fill
                    sizes="40px"
                    className="object-contain"
                  />
                </div>
                <span className="font-montserrat font-bold text-lg text-cerad-green">
                  CERAD-CI
                </span>
              </Link>

              <button
                onClick={() => setIsOpen(false)}
                className="p-2.5 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
                aria-label={t('closeMenu')}
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            {/* Overlay Content */}
            <motion.div
              initial="closed"
              animate="open"
              variants={{
                open: {
                  transition: { staggerChildren: 0.06, delayChildren: 0.1 }
                },
                closed: {
                  transition: { staggerChildren: 0.04, staggerDirection: -1 }
                }
              }}
              className="flex-1 flex flex-col items-center px-6 py-8 overflow-y-auto"
            >
              {/* Navigation Links */}
              <nav className="w-full max-w-xs space-y-2">
                {navLinks.map((link) => {
                  const hasSubmenu = !!link.submenu;
                  const isSubmenuOpen = mobileSubmenuOpen === link.name;
                  const isActive = isLinkActive(link.href, link.submenu);

                  return (
                    <motion.div
                      key={link.name}
                      variants={{
                        open: {
                          y: 0,
                          opacity: 1,
                          transition: { type: 'spring', stiffness: 300, damping: 24 }
                        },
                        closed: {
                          y: 20,
                          opacity: 0
                        }
                      }}
                      className="text-center"
                    >
                      {hasSubmenu ? (
                        /* Item avec sous-menu - bouton toggle */
                        <>
                          <button
                            onClick={() => setMobileSubmenuOpen(isSubmenuOpen ? null : link.name)}
                            className={`w-full py-3 px-6 text-xl font-semibold whitespace-nowrap transition-all flex items-center justify-center gap-2 rounded-2xl ${
                              isActive
                                ? 'text-cerad-green bg-cerad-green/10'
                                : 'text-gray-800 hover:text-cerad-green hover:bg-cerad-green/5'
                            }`}
                          >
                            {isActive && (
                              <span className="w-2 h-2 bg-cerad-green rounded-full animate-pulse" />
                            )}
                            {link.name}
                            <motion.div
                              animate={{ rotate: isSubmenuOpen ? 180 : 0 }}
                              transition={{ duration: 0.2 }}
                            >
                              <ChevronDown className="w-5 h-5" />
                            </motion.div>
                          </button>
                          <AnimatePresence>
                            {isSubmenuOpen && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.2 }}
                                className="overflow-hidden"
                              >
                                <div className="py-2 space-y-1">
                                  {link.submenu?.map((item) => {
                                    const isSubmenuActive = pathname.startsWith(item.href);
                                    return (
                                      <Link
                                        key={item.name}
                                        href={item.href}
                                        onClick={() => {
                                          setIsOpen(false);
                                          setMobileSubmenuOpen(null);
                                        }}
                                        className={`block py-2 px-6 text-sm transition-all rounded-xl ${
                                          isSubmenuActive
                                            ? 'text-cerad-green bg-cerad-green/10 font-semibold'
                                            : 'text-gray-500 hover:text-cerad-green hover:bg-cerad-green/5'
                                        }`}
                                      >
                                        {isSubmenuActive && '• '}
                                        {item.name}
                                      </Link>
                                    );
                                  })}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </>
                      ) : (
                        /* Item sans sous-menu - lien direct */
                        <Link
                          href={link.href}
                          onClick={() => setIsOpen(false)}
                          className={`block py-3 px-6 text-xl font-semibold whitespace-nowrap transition-all rounded-2xl flex items-center justify-center gap-2 ${
                            isActive
                              ? 'text-cerad-green bg-cerad-green/10'
                              : 'text-gray-800 hover:text-cerad-green hover:bg-cerad-green/5'
                          }`}
                        >
                          {isActive && (
                            <span className="w-2 h-2 bg-cerad-green rounded-full animate-pulse" />
                          )}
                          {link.name}
                        </Link>
                      )}
                    </motion.div>
                  );
                })}
              </nav>

              {/* Language Switcher Mobile */}
              <motion.div
                variants={{
                  open: { y: 0, opacity: 1 },
                  closed: { y: 20, opacity: 0 }
                }}
                className="mt-6"
              >
                <LanguageSwitcher variant="mobile" />
              </motion.div>

              {/* Spacer */}
              <div className="flex-1 min-h-8" />
              {/* Social Icons */}
              <motion.div
                variants={{
                  open: { y: 0, opacity: 1 },
                  closed: { y: 20, opacity: 0 }
                }}
                className="flex items-center justify-center gap-4 mt-6"
              >
                {config.facebook && (
                  <a
                    href={config.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-3 bg-gray-100 hover:bg-blue-500 hover:text-white text-gray-600 rounded-xl transition-all"
                    title={`Facebook ${config.companyName}`}
                    aria-label={`Facebook ${config.companyName}`}
                  >
                    <Facebook className="w-5 h-5" />
                  </a>
                )}
                {config.linkedin && (
                  <a
                    href={config.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-3 bg-gray-100 hover:bg-blue-600 hover:text-white text-gray-600 rounded-xl transition-all"
                    title={`LinkedIn ${config.companyName}`}
                    aria-label={`LinkedIn ${config.companyName}`}
                  >
                    <Linkedin className="w-5 h-5" />
                  </a>
                )}
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence >
    </>

  );
}
